import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Trophy, RefreshCw, ArrowUp, ArrowDown, Trash2, Plus, Check, Loader2, Film } from 'lucide-react';
import HomeSettingsPanel from './HomeSettingsPanel';
import { uid } from '../../lib/uid';

const HomeContentPanel = ({ finalists, setFinalists, shorts, setShorts, editions, setEditions, homeSettings, setHomeSettings, onSaveSettings, onSave, onReload, saving, loaded }) => {
  const moveItem = (list, setList, idx, dir) => {
    const next = [...list];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    setList(next);
  };

  const updateFinalist = (idx, field, value) => {
    const next = [...finalists];
    next[idx] = { ...next[idx], [field]: value };
    setFinalists(next);
  };

  const updateShort = (idx, value) => {
    const next = [...shorts];
    next[idx] = { ...next[idx], id: value };
    setShorts(next);
  };

  const updateEdition = (idx, field, value) => {
    const next = [...editions];
    next[idx] = { ...next[idx], [field]: value };
    setEditions(next);
  };

  if (!loaded) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-400">
        <Loader2 size={24} className="animate-spin mr-2" /> Caricamento contenuti home...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <HomeSettingsPanel settings={homeSettings} setSettings={setHomeSettings} onSave={onSaveSettings} saving={saving} />

      {/* FINALISTI 2026 */}
      <div className="p-4 rounded-lg border border-gray-800" style={{ backgroundColor: 'var(--tp-dark)' }}>
        <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
          <div>
            <h3 className="text-white font-bold flex items-center gap-2" style={{ fontFamily: 'Oswald, sans-serif' }}>
              <Trophy size={18} className="text-yellow-500" /> FINALISTI EDIZIONE 2026
            </h3>
            <p className="text-gray-500 text-xs mt-1">
              Nome, link Instagram (opzionale) e badge NEW. Le caselle TBD si aggiungono da sole in home.
            </p>
          </div>
          <Button onClick={onReload} size="sm" variant="outline" className="border-gray-700 text-white hover:bg-gray-800">
            <RefreshCw size={14} />
          </Button>
        </div>

        <div className="space-y-2">
          {finalists.map((f, idx) => (
            <div key={f._uid || idx} className="flex items-center gap-2 flex-wrap sm:flex-nowrap p-2 rounded border border-gray-800 bg-gray-900/50">
              <span className="text-gray-500 text-xs w-6 text-center font-mono">{idx + 1}</span>
              <Input
                data-testid={`finalist-name-input-${idx}`}
                value={f.name || ''}
                onChange={e => updateFinalist(idx, 'name', e.target.value)}
                placeholder="NOME"
                className="bg-gray-900 border-gray-700 text-white font-bold w-32 sm:w-36 text-sm"
              />
              <Input
                data-testid={`finalist-link-input-${idx}`}
                value={f.link || ''}
                onChange={e => updateFinalist(idx, 'link', e.target.value)}
                placeholder="https://www.instagram.com/..."
                className="bg-gray-900 border-gray-700 text-white font-mono text-xs flex-1 min-w-[140px]"
              />
              <label className="flex items-center gap-1.5 text-xs text-gray-400 cursor-pointer select-none whitespace-nowrap">
                <input
                  type="checkbox"
                  data-testid={`finalist-new-checkbox-${idx}`}
                  checked={!!f.isNew}
                  onChange={e => updateFinalist(idx, 'isNew', e.target.checked)}
                  className="accent-red-600"
                />
                NEW
              </label>
              <div className="flex items-center gap-1">
                <button type="button" data-testid={`finalist-up-${idx}`} onClick={() => moveItem(finalists, setFinalists, idx, -1)} disabled={idx === 0}
                  className="p-1.5 rounded bg-gray-800 hover:bg-gray-700 text-gray-300 disabled:opacity-30">
                  <ArrowUp size={13} />
                </button>
                <button type="button" data-testid={`finalist-down-${idx}`} onClick={() => moveItem(finalists, setFinalists, idx, 1)} disabled={idx === finalists.length - 1}
                  className="p-1.5 rounded bg-gray-800 hover:bg-gray-700 text-gray-300 disabled:opacity-30">
                  <ArrowDown size={13} />
                </button>
                <button type="button" data-testid={`finalist-delete-${idx}`} onClick={() => setFinalists(finalists.filter((_, i) => i !== idx))}
                  className="p-1.5 rounded bg-red-900/50 hover:bg-red-800 text-red-400">
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 flex gap-2 flex-wrap">
          <Button
            data-testid="finalist-add-btn"
            onClick={() => setFinalists([...finalists, { _uid: uid(), name: '', link: '', isNew: true }])}
            size="sm" variant="outline" className="border-gray-700 text-white hover:bg-gray-800"
          >
            <Plus size={14} className="mr-1" /> AGGIUNGI FINALISTA
          </Button>
          <Button
            data-testid="finalists-save-btn"
            onClick={() => onSave('finalists')}
            disabled={saving}
            className="bg-orange-600 hover:bg-orange-700 text-white font-bold"
          >
            {saving ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Check size={14} className="mr-2" />}
            SALVA FINALISTI
          </Button>
        </div>
      </div>

      {/* SHORTS */}
      <div className="p-4 rounded-lg border border-gray-800" style={{ backgroundColor: 'var(--tp-dark)' }}>
        <div className="mb-3">
          <h3 className="text-white font-bold flex items-center gap-2" style={{ fontFamily: 'Oswald, sans-serif' }}>
            <Film size={18} className="text-red-500" /> PLAYLIST SHORTS IN HOME
          </h3>
          <p className="text-gray-500 text-xs mt-1">
            Incolla link YouTube Shorts o TikTok completi (o solo l'ID). L'ordine qui è l'ordine di riproduzione.
          </p>
        </div>

        <div className="space-y-2">
          {shorts.map((s, idx) => (
            <div key={s._uid || idx} className="flex items-center gap-2 p-2 rounded border border-gray-800 bg-gray-900/50">
              <span className="text-gray-500 text-xs w-6 text-center font-mono">{idx + 1}</span>
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${s.type === 'tiktok' ? 'bg-cyan-900 text-cyan-300' : 'bg-red-900 text-red-300'}`}>
                {(s.type || 'youtube').toUpperCase()}
              </span>
              <Input
                data-testid={`short-id-input-${idx}`}
                value={s.id || ''}
                onChange={e => updateShort(idx, e.target.value)}
                placeholder="https://youtube.com/shorts/... o ID"
                className="bg-gray-900 border-gray-700 text-white font-mono text-xs flex-1"
              />
              <div className="flex items-center gap-1">
                <button type="button" data-testid={`short-up-${idx}`} onClick={() => moveItem(shorts, setShorts, idx, -1)} disabled={idx === 0}
                  className="p-1.5 rounded bg-gray-800 hover:bg-gray-700 text-gray-300 disabled:opacity-30">
                  <ArrowUp size={13} />
                </button>
                <button type="button" data-testid={`short-down-${idx}`} onClick={() => moveItem(shorts, setShorts, idx, 1)} disabled={idx === shorts.length - 1}
                  className="p-1.5 rounded bg-gray-800 hover:bg-gray-700 text-gray-300 disabled:opacity-30">
                  <ArrowDown size={13} />
                </button>
                <button type="button" data-testid={`short-delete-${idx}`} onClick={() => setShorts(shorts.filter((_, i) => i !== idx))}
                  className="p-1.5 rounded bg-red-900/50 hover:bg-red-800 text-red-400">
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 flex gap-2 flex-wrap">
          <Button
            data-testid="short-add-btn"
            onClick={() => setShorts([...shorts, { _uid: uid(), type: 'youtube', id: '' }])}
            size="sm" variant="outline" className="border-gray-700 text-white hover:bg-gray-800"
          >
            <Plus size={14} className="mr-1" /> AGGIUNGI SHORT
          </Button>
          <Button
            data-testid="shorts-save-btn"
            onClick={() => onSave('shorts')}
            disabled={saving}
            className="bg-red-600 hover:bg-red-700 text-white font-bold"
          >
            {saving ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Check size={14} className="mr-2" />}
            SALVA SHORTS
          </Button>
        </div>
      </div>

      {/* TABELLONI SCORSE EDIZIONI */}
      <div className="p-4 rounded-lg border border-gray-800" style={{ backgroundColor: 'var(--tp-dark)' }}>
        <div className="mb-3">
          <h3 className="text-white font-bold flex items-center gap-2" style={{ fontFamily: 'Oswald, sans-serif' }}>
            <Trophy size={18} className="text-yellow-500" /> TABELLONI SCORSE EDIZIONI
          </h3>
          <p className="text-gray-500 text-xs mt-1">
            Archivio storico del torneo (modale "Tabelloni scorse edizioni" in home). Aggiungi qui la finale 2026 quando si conclude: campi obbligatori Ed. e Vincitore.
          </p>
        </div>

        <div className="space-y-3">
          {editions.map((e, idx) => (
            <div key={e._uid || idx} className="p-3 rounded border border-gray-800 bg-gray-900/50 space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                <Input
                  data-testid={`edition-ed-input-${idx}`}
                  value={e.ed || ''}
                  onChange={ev => updateEdition(idx, 'ed', ev.target.value)}
                  placeholder="Ed. (es. XXI)"
                  className="bg-gray-900 border-gray-700 text-white font-bold w-24 text-sm"
                />
                <Input
                  data-testid={`edition-date-input-${idx}`}
                  value={e.date || ''}
                  onChange={ev => updateEdition(idx, 'date', ev.target.value)}
                  placeholder="Data finale"
                  className="bg-gray-900 border-gray-700 text-white text-sm w-40 sm:w-48"
                />
                <Input
                  data-testid={`edition-location-input-${idx}`}
                  value={e.location || ''}
                  onChange={ev => updateEdition(idx, 'location', ev.target.value)}
                  placeholder="Location"
                  className="bg-gray-900 border-gray-700 text-white text-sm flex-1 min-w-[140px]"
                />
                <div className="flex items-center gap-1 ml-auto">
                  <button type="button" data-testid={`edition-up-${idx}`} onClick={() => moveItem(editions, setEditions, idx, -1)} disabled={idx === 0}
                    className="p-1.5 rounded bg-gray-800 hover:bg-gray-700 text-gray-300 disabled:opacity-30">
                    <ArrowUp size={13} />
                  </button>
                  <button type="button" data-testid={`edition-down-${idx}`} onClick={() => moveItem(editions, setEditions, idx, 1)} disabled={idx === editions.length - 1}
                    className="p-1.5 rounded bg-gray-800 hover:bg-gray-700 text-gray-300 disabled:opacity-30">
                    <ArrowDown size={13} />
                  </button>
                  <button type="button" data-testid={`edition-delete-${idx}`} onClick={() => setEditions(editions.filter((_, i) => i !== idx))}
                    className="p-1.5 rounded bg-red-900/50 hover:bg-red-800 text-red-400">
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Input
                  data-testid={`edition-winner-input-${idx}`}
                  value={e.winner || ''}
                  onChange={ev => updateEdition(idx, 'winner', ev.target.value)}
                  placeholder="1° posto (vincitore)"
                  className="bg-gray-900 border-yellow-700/60 text-yellow-300 font-bold text-sm flex-1 min-w-[140px]"
                />
                <Input
                  data-testid={`edition-runnerup-input-${idx}`}
                  value={e.runnerUp || ''}
                  onChange={ev => updateEdition(idx, 'runnerUp', ev.target.value)}
                  placeholder="2° posto"
                  className="bg-gray-900 border-gray-700 text-white text-sm flex-1 min-w-[140px]"
                />
              </div>
              <Textarea
                data-testid={`edition-others-input-${idx}`}
                value={e.others || ''}
                onChange={ev => updateEdition(idx, 'others', ev.target.value)}
                placeholder="Altri finalisti (separati da virgola)"
                rows={2}
                className="bg-gray-900 border-gray-700 text-gray-300 text-xs"
              />
              <Input
                data-testid={`edition-note-input-${idx}`}
                value={e.note || ''}
                onChange={ev => updateEdition(idx, 'note', ev.target.value)}
                placeholder="Nota (opzionale)"
                className="bg-gray-900 border-gray-700 text-yellow-200/80 italic text-xs"
              />
            </div>
          ))}
        </div>

        <div className="mt-4 flex gap-2 flex-wrap">
          <Button
            data-testid="edition-add-btn"
            onClick={() => setEditions([...editions, { _uid: uid(), ed: '', date: '', location: '', winner: '', runnerUp: '', others: '', note: '' }])}
            size="sm" variant="outline" className="border-gray-700 text-white hover:bg-gray-800"
          >
            <Plus size={14} className="mr-1" /> AGGIUNGI EDIZIONE
          </Button>
          <Button
            data-testid="editions-save-btn"
            onClick={() => onSave('editions')}
            disabled={saving}
            className="bg-yellow-600 hover:bg-yellow-700 text-black font-bold"
          >
            {saving ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Check size={14} className="mr-2" />}
            SALVA EDIZIONI
          </Button>
        </div>
      </div>
    </div>
  );
};

export default HomeContentPanel;
